import React from 'react';
import { PhoneCall, Upload, RefreshCw, Users, MapPin, CheckCircle2, AlertCircle } from 'lucide-react';

// CRM Sales Pipeline — import the calling leads (Excel) and see the pipeline at a glance.
// The actual calling happens in the SW Crew app (roles with "Sales Pipeline" access).
const XLSX_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';

const ensureXLSX = () => new Promise((resolve, reject) => {
  if (window.XLSX) return resolve(window.XLSX);
  const s = document.createElement('script');
  s.src = XLSX_CDN;
  s.onload = () => resolve(window.XLSX);
  s.onerror = () => reject(new Error('Could not load the Excel reader'));
  document.body.appendChild(s);
});

// Map a sheet row (any header casing) to our lead shape.
const pick = (row, keys) => {
  for (const k of Object.keys(row)) {
    if (keys.some(w => k.toLowerCase().replace(/[^a-z]/g, '').includes(w))) return row[k];
  }
  return '';
};

const SECTIONS = [['today', 'Today'], ['missed', 'Missed'], ['upcoming', 'Upcoming'], ['new', 'New'], ['mature', 'Matured'], ['cancelled', 'Cancelled']];

export default function PipelineModule({ apiBaseUrl, canImport = true }) {
  const [counts, setCounts] = React.useState(null);
  const [cities, setCities] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [progress, setProgress] = React.useState('');
  const [replace, setReplace] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const [section, setSection] = React.useState('today');
  const [leads, setLeads] = React.useState([]);
  const [loadingLeads, setLoadingLeads] = React.useState(false);
  const fileRef = React.useRef(null);

  const loadStats = React.useCallback(() => {
    fetch(`${apiBaseUrl}/pipeline.php?action=counts`).then(r => r.json())
      .then(j => setCounts(j.data || null)).catch(() => {});
    fetch(`${apiBaseUrl}/pipeline.php?action=cities`).then(r => r.json())
      .then(j => setCities(Array.isArray(j.data) ? j.data : [])).catch(() => {});
  }, [apiBaseUrl]);

  const loadLeads = React.useCallback((sec) => {
    setLoadingLeads(true);
    fetch(`${apiBaseUrl}/pipeline.php?section=${sec}&limit=300`).then(r => r.json())
      .then(j => setLeads(Array.isArray(j.data) ? j.data : [])).catch(() => setLeads([]))
      .finally(() => setLoadingLeads(false));
  }, [apiBaseUrl]);

  const reopen = (lead) => {
    fetch(`${apiBaseUrl}/pipeline.php?action=outcome`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lead_id: lead.id, outcome: 'reopen', by: 'Office' }),
    }).then(() => { loadLeads(section); loadStats(); }).catch(() => {});
  };

  React.useEffect(() => { loadStats(); }, [loadStats]);
  React.useEffect(() => { loadLeads(section); }, [section, loadLeads]);

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setMsg(null); setBusy(true); setProgress('Reading file…');
    try {
      const XLSX = await ensureXLSX();
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: '' });
      const rows = json.map(r => ({
        name: String(pick(r, ['customername', 'name']) || '').trim(),
        type: String(pick(r, ['customertype', 'type']) || '').trim(),
        cell: String(pick(r, ['cellno', 'cell', 'phone', 'mobile']) || '').trim(),
        city: String(pick(r, ['city']) || '').trim(),
      })).filter(r => r.name || r.cell);

      if (rows.length === 0) { setMsg({ ok: false, text: 'No rows found. Expected columns: CustomerName, CustomerType, CellNo, City.' }); setBusy(false); return; }

      const BATCH = 500;
      let inserted = 0;
      for (let i = 0; i < rows.length; i += BATCH) {
        setProgress(`Importing ${Math.min(i + BATCH, rows.length)} / ${rows.length}…`);
        const body = { rows: rows.slice(i, i + BATCH), replace: replace && i === 0 };
        const res = await fetch(`${apiBaseUrl}/pipeline.php?action=import`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
        }).then(r => r.json());
        inserted += (res.data && res.data.inserted) ? res.data.inserted : 0;
      }
      setMsg({ ok: true, text: `Imported ${inserted} leads from ${file.name}.` });
      loadStats();
    } catch (err) {
      setMsg({ ok: false, text: err.message || 'Import failed' });
    } finally {
      setBusy(false); setProgress('');
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const Stat = ({ label, value, color }) => (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="text-[11px] uppercase tracking-wide font-bold text-slate-400">{label}</div>
      <div className="text-2xl font-extrabold mt-1" style={{ color }}>{value != null ? value.toLocaleString() : '—'}</div>
    </div>
  );

  return (
    <div className="w-full max-w-5xl">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><PhoneCall size={20} className="text-red-500" /> Sales Pipeline</h2>
          <p className="text-xs text-slate-500">Calling leads for the field sales team · managed in the SW Crew app</p>
        </div>
        <button onClick={loadStats} className="px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 text-sm font-semibold inline-flex items-center gap-2">
          <RefreshCw size={15} /> Refresh
        </button>
      </div>

      {/* Counts */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-5">
        <Stat label="Today" value={counts?.today} color="#D97706" />
        <Stat label="Missed" value={counts?.missed} color="#E11D2A" />
        <Stat label="Upcoming" value={counts?.upcoming} color="#2563EB" />
        <Stat label="New" value={counts?.new} color="#64748B" />
        <Stat label="Matured" value={counts?.mature} color="#16A34A" />
        <Stat label="Cancelled" value={counts?.cancelled} color="#9CA3AF" />
      </div>

      {/* Import */}
      {canImport && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 mb-5">
          <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-1"><Upload size={17} className="text-slate-500" /> Import leads (Excel)</h3>
          <p className="text-xs text-slate-500 mb-4">Upload a <b>.xlsx</b> with columns <b>CustomerName, CustomerType, CellNo, City</b>. New rows land in the <b>New</b> section for the team to call.</p>
          <div className="flex items-center gap-3 flex-wrap">
            <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onFile} disabled={busy} className="hidden" id="pipeline-xlsx" />
            <label htmlFor="pipeline-xlsx" className={`px-4 py-2.5 rounded-xl text-sm font-semibold inline-flex items-center gap-2 cursor-pointer ${busy ? 'bg-slate-100 text-slate-400' : 'bg-red-500 text-white hover:bg-red-600'}`}>
              <Upload size={16} /> {busy ? 'Importing…' : 'Choose Excel file'}
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={replace} onChange={e => setReplace(e.target.checked)} disabled={busy} className="w-4 h-4" />
              Replace existing uncalled (New) leads first
            </label>
            {progress && <span className="text-sm text-slate-500 inline-flex items-center gap-2"><RefreshCw size={14} className="animate-spin" />{progress}</span>}
          </div>
          {msg && (
            <div className={`mt-3 text-sm font-medium flex items-center gap-2 ${msg.ok ? 'text-green-700' : 'text-red-600'}`}>
              {msg.ok ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}{msg.text}
            </div>
          )}
        </div>
      )}

      {/* Cities */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-3"><MapPin size={17} className="text-slate-500" /> Leads by city <span className="text-slate-400 font-normal text-sm">(active)</span></h3>
        {cities.length === 0 ? (
          <div className="text-sm text-slate-400 py-6 text-center"><Users size={28} className="mx-auto mb-2 opacity-40" />No leads yet — import an Excel file to start.</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {cities.map(c => (
              <span key={c.city} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-sm text-slate-700">
                {c.city}<span className="px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600 text-xs font-bold">{c.count.toLocaleString()}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Leads by section */}
      <div className="bg-white rounded-xl border border-slate-200 mt-5 overflow-hidden">
        <div className="flex gap-2 p-3 border-b border-slate-100 overflow-x-auto">
          {SECTIONS.map(([k, label]) => (
            <button key={k} onClick={() => setSection(k)}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap border transition-colors ${section === k ? (k === 'missed' ? 'bg-red-500 text-white border-red-500' : 'bg-slate-800 text-white border-slate-800') : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}>
              {label}{counts && counts[k === 'cancelled' ? 'cancelled' : k] != null ? ` (${counts[k === 'cancelled' ? 'cancelled' : k]})` : ''}
            </button>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Customer</th>
                <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">City</th>
                <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Phone</th>
                <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Follow-up</th>
                <th className="text-center px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Calls</th>
                {(section === 'mature' || section === 'cancelled') && <th className="text-right px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Action</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loadingLeads ? (
                <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-400"><RefreshCw size={20} className="mx-auto mb-2 animate-spin" />Loading…</td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-400">No leads in this section.</td></tr>
              ) : leads.map(l => (
                <tr key={l.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5"><div className="font-semibold text-slate-800">{l.name}</div><div className="text-xs text-slate-400">{l.type || '—'}{l.last_remarks ? ` · ${l.last_remarks}` : ''}</div></td>
                  <td className="px-4 py-2.5 text-slate-600">{l.city || '—'}</td>
                  <td className="px-4 py-2.5 font-mono text-slate-600">{l.cell || '—'}</td>
                  <td className="px-4 py-2.5 text-slate-600">{l.followup_date || '—'}</td>
                  <td className="px-4 py-2.5 text-center text-slate-600">{l.attempts || 0}</td>
                  {(section === 'mature' || section === 'cancelled') && (
                    <td className="px-4 py-2.5 text-right">
                      <button onClick={() => reopen(l)} className="text-xs font-semibold text-red-600 hover:underline">Return to pipeline</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
